import React, { useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Box,
  Divider,
  Autocomplete,
} from '@mui/material'
import Grid from '@mui/material/Grid'

import CloseIcon from '@assets/CloseIcon.svg'
import SendIcon from '@assets/SendIcon.svg'
import MessageIcon from '@assets/MessageIcon.svg'
import { useAppTheme } from '@/theme/ThemeContext'

type Payload = {
  recipientName: string
  channel: 'SMS' | 'Email' | 'WhatsApp'
  phone: string
  message: string
  details: any[]
}

const MAX = 200

const NewNotificationDialog: React.FC<{
  open: boolean
  onClose: () => void
  onSend: (payload: Payload) => void
  details?: any[]
}> = ({
  open,
  onClose,
  onSend,
  details = [
    { id: 1, value: 'צרכן נוכחי 0542356433' },
    { id: 2, value: ' בעלי נכס 0537438974' },
  ],
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  // Dark mode colors
  const colors = {
    dialogBg: isDarkMode ? '#162645' : '#FFFFFF',
    titleColor: isDarkMode ? '#fff' : '#0F3E66',
    inputBg: isDarkMode ? '#1A2942' : '#fff',
    inputBorder: isDarkMode ? '#445876' : '#8FB2DB',
    inputBorderHover: isDarkMode ? '#5A7A9E' : '#6A9ED1',
    inputBorderFocus: isDarkMode ? '#3B82F6' : '#4C84C8',
    labelColor: isDarkMode ? '#93A3B5' : '#7A96B8',
    labelFocusColor: isDarkMode ? '#3B82F6' : '#4C84C8',
    dividerColor: isDarkMode ? '#445876' : '#E6ECF2',
    textAreaBorder: isDarkMode ? '#445876' : '#E2E8F0',
    counterColor: isDarkMode ? '#93A3B5' : '#A3AFBF',
    iconFilter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
  }

  const pillInputSx = {
    width: '200px',
    '& .MuiOutlinedInput-root': {
      height: 40,
      borderRadius: '8px',
      backgroundColor: isDarkMode ? '#1F4B8F' : '#fff',
      color: isDarkMode ? '#fff' : undefined,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? '#3B6DB5' : '#8FB2DB',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? '#5A8AD4' : '#6A9ED1',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? '#81B5FF' : '#4C84C8',
    },
    '& .MuiInputLabel-root': {
      color: colors.labelColor,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: colors.labelFocusColor,
    },
    '& .MuiSvgIcon-root': {
      color: isDarkMode ? '#fff' : undefined,
    },
    '& .MuiSelect-icon': {
      color: isDarkMode ? '#fff' : undefined,
    },
  }

  const [form, setForm] = useState<Payload>({
    recipientName: 'מנחם ישראלי',
    channel: 'SMS',
    phone: '0543445478',
    message:
      'שלום מנחם,\nהתקבלה התראה על צריכה חריגה בנכס.\nאנו מטפלים בכך ונעדכן בהקדם.\n\nהמשך יום נעים,\nתאגיד המים שרונים\nרמת השרון',
    details: [],
  })

  const [target, setTarget] = useState<any[]>(details)
  const left = useMemo(() => MAX - (form.message?.length || 0), [form.message])

  const phoneRequired = form.channel === 'SMS'
  const phoneError = phoneRequired && !form.phone.trim()

  const canSend =
    form.recipientName.trim().length > 0 &&
    (!phoneRequired || (phoneRequired && !phoneError)) &&
    left >= 0 &&
    form.message.trim().length > 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      dir="rtl"
      PaperProps={{
        sx: {
          borderRadius: '14px',
          bgcolor: colors.dialogBg,
          boxShadow: '0 10px 28px rgba(16,24,40,0.12)',
        },
      }}
      slotProps={{
        backdrop: { sx: { backdropFilter: 'blur(2px)' } },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.25,
          fontWeight: 700,
          color: colors.titleColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <img style={{ marginLeft: '5px', filter: colors.iconFilter }} src={MessageIcon} alt="message" />
          שליחת הודעה חדשה
        </Box>

        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', left: 8, top: 8 }}
          aria-label="סגור"
        >
          <img style={{ filter: colors.iconFilter }} src={CloseIcon} alt="close" />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ mx: 2, borderColor: colors.dividerColor }} />

      <DialogContent sx={{ py: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid component={Box}>
            <TextField
              fullWidth
              size="small"
              label="שם הנמען"
              value={form.recipientName}
              onChange={e =>
                setForm({ ...form, recipientName: e.target.value })
              }
              sx={pillInputSx}
            />
          </Grid>

          <Grid component={Box}>
            <TextField
              select
              fullWidth
              size="small"
              label={'באמצעות'}
              value={form.channel}
              onChange={e => {
                if (e.target.value === 'Email') {
                  setTarget([
                    {
                      id: 3,
                      value: 'dev@golanshmuel.com',
                    },
                  ])
                } else {
                  setTarget(details)
                }
                setForm({ ...form, channel: e.target.value as any })
              }}
              sx={pillInputSx}
              type={
                form.channel === 'SMS'
                  ? 'tel'
                  : form.channel === 'WhatsApp'
                    ? 'tel'
                    : 'email'
              }
            >
              <MenuItem value="SMS">SMS</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="WhatsApp">WhatsApp</MenuItem>
            </TextField>
          </Grid>

          <Grid component={Box}>
            <Autocomplete
              id="multiple-limit-tags"
              options={target}
              getOptionLabel={(option: any) => option.value}
              renderInput={(params: any) => (
                <TextField {...params} placeholder="בחירה" />
              )}
              sx={pillInputSx}
            />
          </Grid>

          <Grid component={Box} sx={{ width: '100%' }}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                multiline
                minRows={9}
                value={form.message}
                onChange={e =>
                  setForm({ ...form, message: e.target.value.slice(0, MAX) })
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.inputBg,
                    borderRadius: '8px',
                    color: isDarkMode ? '#fff' : undefined,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.textAreaBorder,
                  },
                }}
                placeholder=""
              />
              {/* counter bottom-left inside the field */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 12,
                  bottom: 8,
                  fontSize: 12,
                  color: left <= 0 ? '#B42318' : colors.counterColor,
                }}
              >
                {left}/{MAX}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {/* bottom-left send button */}
      <DialogActions sx={{ px: 3, py: 1.75, justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          disabled={!canSend}
          onClick={() => onSend(form)}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            py: 1,
          }}
        >
          <img style={{ marginLeft: '5px' }} src={SendIcon} alt="send" />
          שליחה
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewNotificationDialog
